if (!customElements.get('mt-grid-product')) {
  customElements.define(
    'mt-grid-product',
    class MtGridProduct extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;

        this.items = Array.from(this.querySelectorAll('.mt-grid-product__item'));
        this.previousButton = this.querySelector('.mt-grid-product__arrow--previous');
        this.nextButton = this.querySelector('.mt-grid-product__arrow--next');
        this.viewport = this.querySelector('.mt-grid-product__viewport');
        this.cta = this.querySelector('.mt-grid-product__cta');
        this.loop = this.dataset.loop !== 'false';
        this.currentIndex = Math.min(
          Math.max(Number.parseInt(this.dataset.initialIndex, 10) || 0, 0),
          Math.max(this.items.length - 1, 0)
        );
        this.dragStartX = null;
        this.dragStartY = null;
        this.dragged = false;

        if (!this.items.length || !this.viewport) return;

        this.onPrevious = () => this.goTo(this.currentIndex - 1);
        this.onNext = () => this.goTo(this.currentIndex + 1);
        this.onResize = () => this.render();
        this.onKeydown = (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.onPrevious();
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.onNext();
          }
        };
        this.onPointerDown = (event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          this.dragStartX = event.clientX;
          this.dragStartY = event.clientY;
          this.dragged = false;
        };
        this.onPointerMove = (event) => {
          if (this.dragStartX === null) return;
          const distanceX = event.clientX - this.dragStartX;
          const distanceY = event.clientY - this.dragStartY;
          if (Math.abs(distanceX) > 8 && Math.abs(distanceX) > Math.abs(distanceY)) {
            this.dragged = true;
          }
        };
        this.onPointerUp = (event) => {
          if (this.dragStartX === null) return;
          const distanceX = event.clientX - this.dragStartX;
          const distanceY = event.clientY - this.dragStartY;
          this.dragStartX = null;
          this.dragStartY = null;

          if (Math.abs(distanceX) > 40 && Math.abs(distanceX) > Math.abs(distanceY)) {
            distanceX < 0 ? this.onNext() : this.onPrevious();
          }
        };
        this.onBlockSelect = (event) => {
          const selectedItem = event.target.closest('.mt-grid-product__item');
          if (!selectedItem || !this.contains(selectedItem)) return;
          this.goTo(this.items.indexOf(selectedItem));
        };

        this.previousButton?.addEventListener('click', this.onPrevious);
        this.nextButton?.addEventListener('click', this.onNext);
        this.addEventListener('keydown', this.onKeydown);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        this.viewport.addEventListener('pointerdown', this.onPointerDown);
        this.viewport.addEventListener('pointermove', this.onPointerMove);
        this.viewport.addEventListener('pointerup', this.onPointerUp);
        this.viewport.addEventListener('pointercancel', this.onPointerUp);
        window.addEventListener('resize', this.onResize);

        this.items.forEach((item, index) => {
          const card = item.querySelector('.mt-grid-product__card');
          card?.addEventListener('click', (event) => {
            if (this.dragged) {
              event.preventDefault();
              this.dragged = false;
              return;
            }
            if (index !== this.currentIndex) {
              this.goTo(index);
              return;
            }

            const href = item.dataset.url;
            if (href) window.location.href = href;
          });
        });

        this.initialized = true;
        this.render();
      }

      disconnectedCallback() {
        if (!this.initialized) return;
        this.previousButton?.removeEventListener('click', this.onPrevious);
        this.nextButton?.removeEventListener('click', this.onNext);
        this.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
        this.viewport?.removeEventListener('pointermove', this.onPointerMove);
        this.viewport?.removeEventListener('pointerup', this.onPointerUp);
        this.viewport?.removeEventListener('pointercancel', this.onPointerUp);
        window.removeEventListener('resize', this.onResize);
        this.initialized = false;
      }

      goTo(index) {
        const lastIndex = this.items.length - 1;
        const previousIndex = this.currentIndex;

        if (this.loop) {
          this.currentIndex = (index + this.items.length) % this.items.length;
        } else {
          this.currentIndex = Math.min(Math.max(index, 0), lastIndex);
        }

        if (this.currentIndex === previousIndex) return;
        this.render();
        this.animateProductChange(previousIndex, this.currentIndex);
      }

      animateProductChange(previousIndex, currentIndex) {
        const leavingItem = this.items[previousIndex];
        const enteringItem = this.items[currentIndex];

        [
          [leavingItem, 'is-arc-leaving'],
          [enteringItem, 'is-arc-entering']
        ].forEach(([item, className]) => {
          if (!item) return;
          window.clearTimeout(item.mtGridProductArcTimer);
          item.classList.remove('is-arc-entering', 'is-arc-leaving');
          void item.offsetWidth;
          item.classList.add(className);
          item.mtGridProductArcTimer = window.setTimeout(() => {
            item.classList.remove(className);
            item.mtGridProductArcTimer = null;
          }, 720);
        });
      }

      getRelativePosition(index) {
        let position = index - this.currentIndex;
        if (!this.loop) return position;

        const half = this.items.length / 2;
        if (position > half) position -= this.items.length;
        if (position < -half) position += this.items.length;
        return position;
      }

      render() {
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        const visibleRadius = isMobile ? 2 : 3;
        const availableWidth = this.viewport.clientWidth;
        // 移动端：选中项 180px 与两侧 132px 的 visual 之间保留 20px 间隙
        const step = isMobile
          ? 180 / 2 + 132 / 2 + 20
          : Math.max(112, Math.min(153, (availableWidth - 180) / 6));

        this.items.forEach((item, index) => {
          const position = this.getRelativePosition(index);
          const isActive = index === this.currentIndex;
          const isVisible = Math.abs(position) <= visibleRadius;
          const card = item.querySelector('.mt-grid-product__card');
          const previousPosition = item.mtGridProductPosition;
          const crossesCarousel =
            previousPosition !== undefined &&
            Math.abs(position - previousPosition) > this.items.length / 2;

          if (crossesCarousel) {
            item.classList.add('is-repositioning');
            if (item.mtGridProductFrame) window.cancelAnimationFrame(item.mtGridProductFrame);
          }

          const finalOffset = position * step;
          const crossingDirection =
            crossesCarousel && position > previousPosition ? 1 : -1;
          const initialOffset = crossesCarousel
            ? (position + crossingDirection) * step
            : finalOffset;

          item.style.setProperty('--mt-grid-product-offset', `${initialOffset}px`);
          item.mtGridProductPosition = position;
          item.classList.toggle('is-active', isActive);
          item.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
          card?.setAttribute('tabindex', isVisible ? '0' : '-1');
          if (card) card.disabled = isActive && !item.dataset.url;

          if (crossesCarousel) {
            void item.offsetWidth;
            item.mtGridProductFrame = window.requestAnimationFrame(() => {
              item.classList.remove('is-repositioning');
              item.style.setProperty('--mt-grid-product-offset', `${finalOffset}px`);
              item.mtGridProductFrame = null;
            });
          }
        });

        const activeItem = this.items[this.currentIndex];
        if (this.cta && activeItem) {
          const href = activeItem.dataset.url;
          this.cta.hidden = !href;
          if (href) this.cta.href = href;
          this.cta.setAttribute('aria-label', `${this.cta.dataset.label}: ${activeItem.dataset.title}`);
        }

        if (this.previousButton) {
          this.previousButton.hidden = this.items.length < 2;
          this.previousButton.disabled = !this.loop && this.currentIndex === 0;
        }
        if (this.nextButton) {
          this.nextButton.hidden = this.items.length < 2;
          this.nextButton.disabled = !this.loop && this.currentIndex === this.items.length - 1;
        }

        this.dispatchEvent(
          new CustomEvent('mt:grid-product:change', {
            bubbles: true,
            detail: { index: this.currentIndex }
          })
        );
      }
    }
  );
}
